import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, Button, Typography, Chip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelIcon from '@mui/icons-material/Cancel';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useLeadDetails } from '../../../contexts/LeadDetailsContext';
import AddNotes from '../../../components/common/AddNotes';
import CreateNewTask from '../../../components/common/CreateNewTask';
import PreferenceUpdate from '../../../components/common/PreferenceUpdate';
import InterestsUpdate from '../../../components/common/InterestsUpdate';
import TagsUpdate from '../../../components/common/TagsUpdate';
import IntentsUpdate from '../../../components/common/IntentsUpdate';
import AmenitiesUpdate from '../../../components/common/AmenitiesUpdate';
import LocationUpdate from '../../../components/common/LocationUpdate';
import AudioPlayer from '../../../components/common/AudioPlayer';
import UpdateStatus from '../../../components/common/UpdateStatus';
import { fetchProjectUnitSelect } from '../../../services/common';
import {
  buildLeadOverviewCopilotModel,
  getLeadDialNumber,
  getRecoveryReadinessBarsFromOverview,
  normalizeOverviewLeadRecovery,
} from './components/leadOverviewCopilotModel';
import {
  formatHeadingTitleCase,
  LeadCoverage,
  LeadRecentNotes,
  LeadRecovery,
  LeadRestoreComponent,
  LeadTemperature,
  normalizeLeadConversionForCard,
  conversionBandFromScore,
  OverviewCardTitleWithIcon,
} from './components/overview';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  teal: '#0E9F7E',
  tealLight: '#ECFDF5',
  tealDark: '#065F46',
  slate: '#0F172A',
  slate2: '#1E293B',
  slate3: '#64748B',
  slate4: '#94A3B8',
  border: '#E2E8F0',
  borderSubtle: '#F1F5F9',
  bg: '#F8FAFC',
  bg2: '#F1F5F9',
  white: '#FFFFFF',
  red: '#EF4444',
  redLight: '#FEF2F2',
  redDark: '#991B1B',
  amber: '#D97706',
  amberLight: '#FFFBEB',
  green: '#22C55E',
  greenLight: '#DCFCE7',
  greenDark: '#166534',
  blue: '#2563EB',
  blueLight: '#EFF6FF',
  purple: '#7C3AED',
  purpleLight: '#F5F3FF',
  /** Peach badges (Recoverable, Score N, Recovery) — matches reference */
  peachBadgeBg: '#FFFBEB',
  peachBadgeFg: '#92400E',
  peachBadgeBorder: '#FDE68A',
  /** Brown hero / score pill (recovery %, 61/100) — earthy, not bright red */
  scoreBrown: '#78350F',
  scoreBrownMuted: '#A16207',
};

// ─── Unified typography tokens — applied consistently across ALL cards ────────
const T = {
  // Card section eyebrow: "LEAD RECOVERY", "LEAD TEMPERATURE", "RECENT CALLS"
  cardHeader: {
    fontSize: '10px',
    fontWeight: 800,
    letterSpacing: '0.08em',
    color: C.slate3,
    textTransform: 'uppercase',
  },
  // Large hero metric: "61%", "Warm", "24d"
  heroMetric: {
    fontSize: '34px',
    fontWeight: 900,
    lineHeight: 1,
    letterSpacing: '-0.02em',
  },
  // Chip badge: "Recoverable", "Score 62", "Junk stage", "RECOVERY"
  chip: { height: 22, fontSize: '0.7rem', fontWeight: 800, borderRadius: '999px' },
  // Primary body text (bold): descriptions, key labels
  bodyPrimary: { fontSize: '12px', fontWeight: 700, color: C.slate2, lineHeight: 1.45 },
  // Secondary body text: supporting details
  bodySecondary: { fontSize: '12px', fontWeight: 600, color: C.slate3, lineHeight: 1.45 },
  // Data row label: "Created", "First contact", etc.
  rowLabel: { fontSize: '11px', color: C.slate4, fontWeight: 500 },
  // Data row value: dates, counts
  rowValue: { fontSize: '12px', fontWeight: 700, color: C.slate2 },
  // Progress bar row label: "Engagement", "Recency"
  barLabel: { fontSize: '12px', color: C.slate3, fontWeight: 700 },
  // Progress bar score value: "82", "100"
  barValue: { fontSize: '12px', color: C.slate2, fontWeight: 700, textAlign: 'right' },
  // Axis labels: "Cold" / "Hot"
  axisLabel: { fontSize: '11px', color: C.slate4, fontWeight: 600 },
  // Small uppercase section divider inside a card
  innerLabel: {
    fontSize: '10px',
    fontWeight: 800,
    color: C.slate4,
    letterSpacing: '0.08em',
  },
  /** Card header (title case, not ALL CAPS) */
  cardTitle: {
    fontSize: '12px',
    fontWeight: 800,
    letterSpacing: '0.01em',
    color: C.slate2,
    textTransform: 'none',
    lineHeight: 1.35,
  },
  /** Recent snapshot cards (calls / notes / task / activity) */
  snapshotTitle: { fontSize: '12px', fontWeight: 700, color: C.slate2, lineHeight: 1.35 },
  snapshotMeta: { fontSize: '12px', fontWeight: 500, color: C.slate2, lineHeight: 1.45 },
  snapshotBody: { fontSize: '12px', fontWeight: 500, color: C.slate2, lineHeight: 1.5 },
};

const PREF_GRID_GAP = '10px';

/** Top overview row: Latest note + Preferences — same shell & header rhythm for every lead */
const EXEC_OVERVIEW_ROW_CARD_SX = {
  bgcolor: C.white,
  border: `1px solid ${C.border}`,
  borderRadius: '14px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  p: '18px 20px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minHeight: 0,
};
const EXEC_OVERVIEW_HEADER_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  minHeight: 40,
  flexShrink: 0,
};
/** Keeps title column aligned when left card has optional “+ Add Note” */
const EXEC_OVERVIEW_HEADER_ACTION_SLOT_SX = {
  minWidth: 92,
  display: 'flex',
  justifyContent: 'flex-end',
  flexShrink: 0,
};

/** Page shell — gives the tab a clear “insight dashboard” hierarchy */
const InsightPageHeader = ({ eyebrow, title, subtitle, badge }) => (
  <Box sx={{ mb: 2.5, maxWidth: 800 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: eyebrow || badge ? '8px' : '6px' }}>
      {eyebrow ? (
        <Typography sx={{ ...T.cardHeader, color: C.teal, mb: 0 }}>{eyebrow}</Typography>
      ) : null}
      {badge}
    </Box>
    <Typography
      sx={{
        fontSize: { xs: '20px', sm: '22px' },
        fontWeight: 900,
        color: C.slate,
        letterSpacing: '-0.03em',
        lineHeight: 1.2,
        mb: subtitle ? '8px' : 0,
      }}
    >
      {title}
    </Typography>
    {subtitle ? (
      <Typography sx={{ ...T.bodySecondary, fontSize: '13px', maxWidth: 640, fontWeight: 500 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

/** Normalise stage string for comparisons (e.g. "Site Visit" → "site_visit"). */
const normalizeStageKey = (s) => String(s || '').toLowerCase().replace(/\s+/g, '_').trim();

/** Merge API `contact` phone fields into `contact_info` for dial / copilot helpers. */
const mergeDialNumberIntoLeadContactInfo = (lead) => {
  if (!lead) return null;
  const c = lead.contact || {};
  return {
    ...lead,
    contact_info: {
      ...(lead.contact_info || {}),
      mobile: lead.contact_info?.mobile || c.mobile,
      phone: lead.contact_info?.phone || c.phone,
    },
  };
};

// ─── Recent activity cards (snapshots) — mappers match overview API shape ───

const MONTH_LONG_TO_SHORT = {
  january: 'Jan',
  february: 'Feb',
  march: 'Mar',
  april: 'Apr',
  may: 'May',
  june: 'Jun',
  july: 'Jul',
  august: 'Aug',
  september: 'Sep',
  october: 'Oct',
  november: 'Nov',
  december: 'Dec',
};

const abbreviateLongMonthNamesInString = (s) => {
  if (!s) return '';
  let out = String(s);
  Object.entries(MONTH_LONG_TO_SHORT).forEach(([longForm, shortForm]) => {
    out = out.replace(new RegExp(`\\b${longForm}\\b`, 'gi'), shortForm);
  });
  return out;
};

/** e.g. "06:09 PM" → "6:09 PM" */
const stripClockHourLeadingZero = (str) =>
  String(str || '').replace(/\b0(\d{1,2}:\d{2}\s*(?:AM|PM)?)\b/gi, '$1');

/** One readable line: "Mar 29, 2026 · 6:09 PM" (API mixes "March …" / "Mar …" / embedded time). */
const formatSnapshotDateLine = (datePart, timePart) => {
  const time = String(timePart || '').trim();
  let date = String(datePart || '').trim();
  if (!date) return time ? stripClockHourLeadingZero(abbreviateLongMonthNamesInString(time)) : '—';
  date = abbreviateLongMonthNamesInString(date);
  // Single field already contains clock: "Mar 29, 2026 06:09 PM"
  if (!time && /\b\d{1,2}:\d{2}\s*(AM|PM)\b/i.test(date)) {
    return stripClockHourLeadingZero(date.replace(/\s+(\d{1,2}:\d{2}\s*(?:AM|PM))\b/i, ' · $1'));
  }
  if (time) return stripClockHourLeadingZero(`${date} · ${time}`);
  return date;
};

const formatCallSecondsAsMmSs = (rc) => {
  const n = rc?.duration_num;
  if (typeof n === 'number' && !Number.isNaN(n) && n >= 0) {
    const m = Math.floor(n / 60);
    const sec = Math.floor(n % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  }
  const dur = String(rc?.duration || '').trim().toLowerCase();
  let totalSec = 0;
  const mMatch = dur.match(/(\d+)\s*m\b/);
  const sMatch = dur.match(/(\d+)\s*s\b/);
  if (mMatch) totalSec += parseInt(mMatch[1], 10) * 60;
  if (sMatch) totalSec += parseInt(sMatch[1], 10);
  if (totalSec > 0) {
    const m = Math.floor(totalSec / 60);
    const sec = Math.floor(totalSec % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  }
  const raw = dur.replace(/\s*s$/i, '').trim();
  const parsed = parseInt(raw, 10);
  if (!Number.isNaN(parsed) && parsed >= 0) {
    const m = Math.floor(parsed / 60);
    const sec = Math.floor(parsed % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  }
  return '0:00';
};

const mapApiRecentCallToViewModel = (rc) => {
  if (!rc) return null;
  const typeMap = { outgoing: 'Outgoing', incoming: 'Incoming', missed: 'Missed' };
  const recordingUrl = rc.recording_url || rc.recordingUrl || null;
  return {
    agent: rc.agent?.name || 'Unknown',
    team: typeof rc.agent?.team === 'string' ? rc.agent.team : rc.agent?.team?.name || '',
    dateLine: formatSnapshotDateLine(rc.date, null),
    duration: rc.duration || '—',
    status: typeMap[rc.call_type?.toLowerCase()] || rc.call_type || '—',
    callType: rc.call_type?.toLowerCase(),
    recordingUrl: recordingUrl && String(recordingUrl).trim() ? String(recordingUrl).trim() : null,
    durationLabel: formatCallSecondsAsMmSs(rc),
  };
};

const mapApiNoteToViewModel = (n) => {
  if (!n) return null;
  const initials = n.created_by
    ? n.created_by.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';
  return {
    initials,
    author: n.created_by || 'Unknown',
    time: formatSnapshotDateLine(n.date, n.time),
    text: n.description,
  };
};

const mapApiTaskToViewModel = (t) => {
  if (!t) return null;
  return {
    title: t.title,
    desc: t.description,
    date: formatSnapshotDateLine(t.date, t.time),
    status: t.status_verbose || t.status,
    scheduleMissed: Boolean(t.schedule_missed),
  };
};

const mapApiActivityToViewModel = (a) => {
  if (!a) return null;
  const titleLower = (a.title || '').toLowerCase();
  let dot = 'status';
  let emoji = '🔄';
  if (titleLower.includes('note')) { dot = 'note'; emoji = '📝'; }
  if (titleLower.includes('call')) { dot = 'call'; emoji = '📞'; }
  if (titleLower.includes('task')) { dot = 'task'; emoji = '✅'; }
  const title = String(a.title || '').trim();
  const description = String(a.description || '').trim();
  const both = Boolean(title && description);
  const activityTitle = both && title !== description ? title : null;
  const activityBody = description || (!activityTitle ? title : '') || null;
  return {
    dot,
    emoji,
    activityTitle,
    activityBody,
    time: formatSnapshotDateLine(a.date, a.time),
  };
};

/** Prefer call summary from API; fall back to latest activity description when it is call-related. */
const getRecentCallSummaryText = (recentCall, latestActivity) => {
  if (!recentCall) return '';
  let detailText = String(recentCall.summary ?? '').trim();
  if (!detailText && latestActivity) {
    const t = String(latestActivity.title || '').toLowerCase();
    if (t.includes('call')) {
      detailText = String(latestActivity.description || '').trim();
    }
  }
  return detailText;
};

const DeletedNotRecoverableInsightCard = ({ recovery }) => {
  if (!recovery) return null;
  const score = recovery.recovery_score ?? 0;
  const ev = recovery.recovery_evidence || [];
  const extra = ev.length ? '' : 'No recovery evidence available.';
  const body = [recovery.reason, extra].filter(Boolean).join(' ');

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: '1px solid #FECACA',
        bgcolor: '#FFF1F2',
        p: '14px 16px',
        mb: 2.25,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '999px',
            bgcolor: '#DC2626',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mt: '2px',
          }}
        >
          <CancelIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#991B1B', lineHeight: 1.25, mb: '6px' }}>
            Not recoverable — AI detected clear disinterest
          </Typography>
          <Typography sx={{ ...T.bodyPrimary, fontWeight: 500, color: '#7F1D1D', lineHeight: 1.45 }}>
            {body}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: 1.25, py: 0.75, borderRadius: '999px', bgcolor: C.white, border: '1px solid #FECACA', flexShrink: 0 }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 800, color: '#B91C1C', whiteSpace: 'nowrap' }}>
          Score {Math.round(score)} / 100
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Sticky next action ───────────────────────────────────────────────────────

const StickyNextActionBar = ({ copilot, onAction, show, leadStage, upcomingTask }) => {
  if (!show || !copilot) return null;
  const { status, primaryNextAction } = copilot;
  const tone = status.tone || 'amber';
  const barStyle =
    tone === 'red'
      ? { border: '#FECACA', bg: '#FFF5F5', dot: C.redDark, labelColor: C.slate2 }
      : tone === 'green'
        ? { border: '#BBF7D0', bg: '#ECFDF5', dot: C.tealDark, labelColor: C.tealDark }
        : { border: '#FDE68A', bg: C.amberLight, dot: '#92400E', labelColor: C.slate2 };

  const p = primaryNextAction;
  const isClosureStage = normalizeStageKey(leadStage) === 'closure';
  const hasUpcomingEvent = Boolean(upcomingTask?.title);

  const upcomingWhen = upcomingTask
    ? formatSnapshotDateLine(upcomingTask.date, upcomingTask.time)
    : '';

  const nextTitle = isClosureStage
    ? ''
    : hasUpcomingEvent
      ? upcomingTask.title
      : '';
  const nextDetailLine = isClosureStage
    ? ''
    : hasUpcomingEvent
      ? upcomingWhen
      : '';

  const scheduleMissed = hasUpcomingEvent ? Boolean(upcomingTask?.schedule_missed) : false;

  const richPrimary = p && (String(p.title || '').trim() || String(p.detail || '').trim() || String(p.suggested_action || '').trim());

  const formatStickyBarText = (s) => stripClockHourLeadingZero(abbreviateLongMonthNamesInString(String(s || '').trim()));

  const buildStickyBarActionDescriptors = () => {
    if (isClosureStage) return [];

    const nextType = upcomingTask?.item_type || '';
    const stage = normalizeStageKey(leadStage);

    let primaryKey = 'lead_followup';
    if (nextType === 'site_visit') primaryKey = 'site_visit';
    else if (nextType === 'document_collection') primaryKey = 'document_collection';
    else if (!upcomingTask && stage === 'site_visit') primaryKey = 'site_visit';

    const secondaryKey = primaryKey === 'document_collection' ? 'lead_followup' : 'document_collection';

    return [
      {
        key: primaryKey,
        label:
          primaryKey === 'lead_followup'
            ? 'Schedule follow-up'
            : primaryKey === 'site_visit'
              ? 'Schedule site visit'
              : 'Share documents',
      },
      {
        key: secondaryKey,
        label: secondaryKey === 'document_collection' ? 'Share documents' : 'Schedule follow-up',
      },
    ];
  };

  const stickyButtons = buildStickyBarActionDescriptors();

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 6,
        mb: '18px',
        py: '12px',
        px: '14px',
        borderRadius: '12px',
        border: `1px solid ${barStyle.border}`,
        bgcolor: barStyle.bg,
        boxShadow: '0 4px 20px rgba(15,23,42,0.06)',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1, minWidth: 220 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '999px', bgcolor: barStyle.dot, mt: '6px', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          {isClosureStage ? (
            <Box sx={{ display: 'grid', gap: '6px' }}>
              <Typography sx={{ ...T.cardHeader, color: barStyle.labelColor, mb: '0px' }}>
                Final stage
              </Typography>
              <Typography sx={{ ...T.bodyPrimary, fontWeight: 700, color: C.slate2, lineHeight: 1.45 }}>
                No further action required
              </Typography>
            </Box>
          ) : nextTitle ? (
            <Box sx={{ display: 'grid', gap: '6px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0 }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 800,
                    color: barStyle.labelColor,
                    lineHeight: 1.35,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {formatStickyBarText(nextTitle)}
                </Typography>
                {scheduleMissed ? (
                  <Chip
                    label="Past scheduled time (IST)"
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: '#FFEDD5',
                      color: '#9A3412',
                      border: '1px solid #FDBA74',
                    }}
                  />
                ) : null}
              </Box>
              {nextDetailLine ? (
                <Typography sx={{ ...T.bodyPrimary, fontWeight: 500, lineHeight: 1.45 }}>
                  {formatStickyBarText(nextDetailLine)}
                </Typography>
              ) : null}
              {hasUpcomingEvent && p?.suggested_action && richPrimary ? (
                <Typography sx={{ ...T.bodyPrimary, fontWeight: 500, lineHeight: 1.45 }}>
                  <Box component="span" sx={{ ...T.innerLabel, color: C.slate4, display: 'block', mb: '2px' }}>
                    Suggested
                  </Box>
                  {formatStickyBarText(p.suggested_action)}
                </Typography>
              ) : null}
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gap: '6px' }}>
              <Typography sx={{ ...T.cardHeader, color: barStyle.labelColor, mb: '4px' }}>
                Next action:
              </Typography>
              <Typography sx={{ ...T.bodyPrimary, fontWeight: 700, lineHeight: 1.45 }}>
                not scheduled
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {stickyButtons.length ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', flexShrink: 0 }}>
          {stickyButtons.slice(0, 2).map((a) => {
            const contained = a.key === 'lead_followup' || a.key === 'site_visit' || a.key === 'document_collection';
            return (
              <Button
                key={`${a.key}-${a.label}`}
                variant={contained ? 'contained' : 'outlined'}
                size="small"
                onClick={() => onAction(a.key)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '12px',
                  borderRadius: '10px',
                  ...(contained
                    ? { bgcolor: C.teal, color: '#fff', '&:hover': { bgcolor: C.tealDark } }
                    : {
                        borderColor: tone === 'green' ? '#86EFAC' : C.border,
                        color: C.slate2,
                      }),
                }}
              >
                {a.label}
              </Button>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
};

const ConversationInsightsTop = ({ conversationInsights, onPrimaryAction }) => {
  const alerts = conversationInsights?.alerts || [];
  if (!alerts.length) return null;

  const conversationInsightAlertStyle = (severity) => {
    const sev = String(severity || 'medium').toLowerCase();
    if (sev === 'high') return { border: '#FCA5A5', fg: '#991B1B', iconBg: '#FEF2F2' };
    if (sev === 'low') return { border: '#86EFAC', fg: '#166534', iconBg: '#ECFDF5' };
    return { border: '#F5D38F', fg: '#8B5A00', iconBg: '#FFFBEB' };
  };

  return (
    <Box sx={{ mb: 2, display: 'grid', gap: '10px' }}>
      {alerts.slice(0, 5).map((a, idx) => {
        const style = conversationInsightAlertStyle(a?.severity);
        return (
          <Box key={`${idx}-${a?.type || 'alert'}`} sx={{ borderRadius: '12px', border: `1px solid ${style.border}`, bgcolor: C.white, p: '12px 14px' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <Box
                sx={{
                  mt: '2px',
                  width: 28,
                  height: 28,
                  borderRadius: '999px',
                  bgcolor: style.iconBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '999px', bgcolor: style.fg }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ ...T.bodyPrimary, color: C.slate2, fontWeight: 700 }}>
                  {formatHeadingTitleCase(a?.title || 'Insight')}
                </Typography>
                {a?.detail ? (
                  <Typography sx={{ ...T.bodyPrimary, fontWeight: 500, mt: '3px' }}>{a.detail}</Typography>
                ) : null}
                {a?.suggested_action ? (
                  <Box sx={{ mt: '6px' }}>
                    <Typography sx={{ ...T.innerLabel, color: C.slate4, fontSize: '10px', textTransform: 'none', mb: '3px' }}>
                      Next suggested action
                    </Typography>
                    <Typography sx={{ ...T.bodyPrimary, fontWeight: 500 }}>{a.suggested_action}</Typography>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Recent activity (snapshots) ──────────────────────────────────────────────

const OverviewSection = ({ eyebrow, title, subtitle, children, compact = false, sectionIcon, sectionIconBg }) => (
  <Box component="section" sx={{ mt: compact ? '20px' : '28px' }}>
    {eyebrow ? (
      <Typography sx={{ ...T.cardHeader, color: C.teal, mb: '6px' }}>
        {eyebrow}
      </Typography>
    ) : null}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        mb: subtitle ? '6px' : compact ? '10px' : '14px',
      }}
    >
      {sectionIcon ? (
        <Box
          sx={{
            width: compact ? 28 : 32,
            height: compact ? 28 : 32,
            borderRadius: '8px',
            bgcolor: sectionIconBg || C.bg2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: compact ? '14px' : '16px',
            flexShrink: 0,
          }}
        >
          {sectionIcon}
        </Box>
      ) : null}
      <Typography
        sx={{
          fontSize: compact ? '15px' : '17px',
          fontWeight: 800,
          color: C.slate2,
          letterSpacing: '-0.02em',
        }}
      >
        {formatHeadingTitleCase(title)}
      </Typography>
    </Box>
    {subtitle ? (
      <Typography sx={{ ...T.bodyPrimary, fontWeight: 500, mb: '14px', maxWidth: 800 }}>
        {subtitle}
      </Typography>
    ) : null}
    {children}
  </Box>
);

// ─── OverviewSnapshotCard — snapshot cards: separated header row (icon + uppercase eyebrow + action) ─
const OverviewSnapshotCard = ({ iconBg, iconEmoji, title, action, actionLabel, children }) => (
  <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '16px', pt: '14px', pb: '12px', borderBottom: `1px solid ${C.borderSubtle}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>
          {iconEmoji}
        </Box>
        <Typography sx={{ ...T.cardTitle }}>{formatHeadingTitleCase(title)}</Typography>
      </Box>
      {action && (
        <Typography component="button" type="button" onClick={action} sx={{ fontSize: '11px', fontWeight: 700, color: C.teal, cursor: 'pointer', border: 'none', bgcolor: 'transparent', fontFamily: 'inherit', p: 0, flexShrink: 0, '&:hover': { textDecoration: 'underline' } }}>
          {actionLabel}
        </Typography>
      )}
    </Box>
    <Box sx={{ px: '16px', py: '14px', flex: 1 }}>{children}</Box>
  </Box>
);

const RecentCalls = ({ calls, onViewAll }) => (
  <OverviewSnapshotCard iconBg="#DCFCE7" iconEmoji="📞" title="Recent Calls" action={onViewAll} actionLabel="View all ›">
    {!calls?.length ? (
      <Typography sx={{ ...T.snapshotBody, fontStyle: 'italic', color: C.slate4 }}>No recent calls</Typography>
    ) : (
      calls.map((c, i) => {
        const isMissed = c.callType === 'missed' || c.status?.toLowerCase() === 'missed';
        return (
          <Box
            key={i}
            sx={{
              py: '9px',
              borderBottom: i < calls.length - 1 ? `1px solid ${C.borderSubtle}` : 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: isMissed ? '#FEE2E2' : '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 }}>📞</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ ...T.snapshotTitle }}>{c.agent}</Typography>
                <Typography sx={{ ...T.snapshotMeta, mt: '3px' }}>
                  {c.dateLine}
                  {c.duration && c.duration !== '—' ? ` · ${c.duration}` : ''}
                </Typography>
              </Box>
              <Box sx={{ fontSize: '10px', fontWeight: 700, px: '8px', py: '3px', borderRadius: '10px', bgcolor: isMissed ? '#FEE2E2' : '#DCFCE7', color: isMissed ? C.redDark : '#166534', whiteSpace: 'nowrap', flexShrink: 0, textTransform: 'capitalize' }}>
                {c.status}
              </Box>
            </Box>
            {c.detailText ? (
              <Typography sx={{ ...T.snapshotBody, mt: '10px', ml: { xs: 0, sm: '42px' } }}>{c.detailText}</Typography>
            ) : null}
            {c.recordingUrl ? (
              <Box sx={{ mt: 1.25, ml: { xs: 0, sm: '42px' } }}>
                <AudioPlayer audioUrl={c.recordingUrl} duration={c.durationLabel} sx={{ px: 1.5, pt: 1, pb: 1 }} />
              </Box>
            ) : null}
          </Box>
        );
      })
    )}
  </OverviewSnapshotCard>
);

/** Same rows as Recent Notes snapshot — reused for executive “Latest note” */
const OverviewNotesListBody = ({ notes }) => (
  <>
    {!notes?.length ? (
      <Typography sx={{ ...T.snapshotBody, fontStyle: 'italic', color: C.slate4 }}>No notes added yet</Typography>
    ) : null}
    {notes.map((n, i) => (
      <Box key={i} sx={{ display: 'flex', gap: '10px', py: '9px', borderBottom: i < notes.length - 1 ? `1px solid ${C.borderSubtle}` : 'none' }}>
        <Box sx={{ width: 3, borderRadius: '2px', bgcolor: '#F59E0B', flexShrink: 0, alignSelf: 'stretch' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '2px' }}>
            <Box sx={{ width: 22, height: 22, borderRadius: '50%', background: 'linear-gradient(135deg,#E24B4A,#C93D3C)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700, color: '#fff', flexShrink: 0 }}>
              {n.initials}
            </Box>
            <Typography sx={{ ...T.snapshotTitle }}>{n.author}</Typography>
          </Box>
          <Typography sx={{ ...T.snapshotMeta, mb: '6px' }}>{n.time}</Typography>
          <Typography sx={{ ...T.snapshotBody }}>{n.text}</Typography>
        </Box>
      </Box>
    ))}
  </>
);

const RecentNotes = ({ notes, onAddNote }) => (
  <OverviewSnapshotCard iconBg="#FEF3C7" iconEmoji="📝" title="Recent Notes" action={onAddNote} actionLabel="+ Add Note">
    <OverviewNotesListBody notes={notes} />
  </OverviewSnapshotCard>
);

const UpcomingTask = ({ tasks, onNewTask }) => (
  <OverviewSnapshotCard iconBg={C.blueLight} iconEmoji="✅" title="Upcoming Task" action={onNewTask} actionLabel="+ New Task">
    {!tasks?.length ? (
      <Typography sx={{ ...T.snapshotBody, fontStyle: 'italic', color: C.slate4 }}>No upcoming tasks</Typography>
    ) : null}
    {tasks.map((t, i) => (
      <Box key={i} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', py: '9px', borderBottom: i < tasks.length - 1 ? `1px solid ${C.borderSubtle}` : 'none' }}>
        <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${C.teal}`, flexShrink: 0, mt: '1px' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...T.snapshotTitle, mb: '4px' }}>{t.title}</Typography>
          {t.desc && <Typography sx={{ ...T.snapshotBody, mb: '8px' }}>{t.desc}</Typography>}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Typography sx={{ ...T.snapshotMeta, display: 'flex', alignItems: 'center', gap: '4px' }}>
              📅 {t.date}
            </Typography>
            {t.scheduleMissed ? (
              <Chip
                label="Past scheduled (IST)"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6rem',
                  fontWeight: 800,
                  bgcolor: '#FFEDD5',
                  color: '#9A3412',
                  border: '1px solid #FDBA74',
                }}
              />
            ) : null}
            <Box sx={{ fontSize: '10px', fontWeight: 700, px: '7px', py: '3px', borderRadius: '6px', bgcolor: '#EFF6FF', color: '#1E40AF', textTransform: 'capitalize' }}>{t.status}</Box>
          </Box>
        </Box>
      </Box>
    ))}
  </OverviewSnapshotCard>
);

const DOT_STYLES = {
  status: { bg: C.tealLight },
  call: { bg: '#DCFCE7' },
  task: { bg: C.amberLight },
  note: { bg: C.blueLight },
};

const resolvePreferenceChipLabel = (item) =>
  (typeof item === 'string' ? item : (item?.name || item?.label || item?.value || '')) || '';

const OverviewChipList = ({ items, color, bgColor, borderColor }) => {
  if (!items?.length) {
    return (
      <Typography sx={{ ...T.bodyPrimary, fontWeight: 500, fontStyle: 'italic', color: C.slate2, mt: 0 }}>
        None specified
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: 0 }}>
      {items.map((item, i) => {
        const lbl = resolvePreferenceChipLabel(item);
        if (!lbl) return null;
        return (
          <Chip
            key={i}
            label={lbl}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.625rem',
              fontWeight: 600,
              borderRadius: '10px',
              bgcolor: bgColor || `${color}15`,
              color,
              border: `1px solid ${borderColor || `${color}30`}`,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        );
      })}
    </Box>
  );
};

// ─── OverviewAttributeCard — header unified ───────────────────────────────────
const OverviewAttributeCard = ({
  title,
  iconEmoji,
  iconBg,
  accentColor,
  actionLabel,
  onAction,
  actionColor,
  readOnly = false,
  children,
}) => (
  <Box
    sx={{
      bgcolor: readOnly ? '#FAFBFC' : C.white,
      border: `1px solid ${readOnly ? '#E8ECF1' : C.border}`,
      borderRadius: '10px',
      boxShadow: readOnly ? 'none' : '0 1px 3px rgba(0,0,0,0.04)',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      height: 'auto',
      overflow: 'hidden',
      position: 'relative',
      opacity: readOnly ? 0.98 : 1,
      ...(accentColor
        ? {
            '&:before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              bgcolor: accentColor,
            },
          }
        : {}),
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.75,
        px: '10px',
        pt: '8px',
        pb: '7px',
        borderBottom: `1px solid ${C.borderSubtle}`,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
        {iconEmoji ? (
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '6px',
              bgcolor: iconBg || C.tealLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              flexShrink: 0,
            }}
          >
            {iconEmoji}
          </Box>
        ) : null}
        <Typography sx={{ ...T.cardTitle, fontSize: '11px' }}>{formatHeadingTitleCase(title)}</Typography>
      </Box>
      {readOnly ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          <LockOutlinedIcon sx={{ fontSize: 14, color: C.slate4 }} />
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.slate4, letterSpacing: '0.02em' }}>
            View only
          </Typography>
        </Box>
      ) : onAction && actionLabel ? (
        <Typography
          component="button"
          type="button"
          onClick={onAction}
          sx={{
            fontSize: '10px',
            fontWeight: 700,
            color: actionColor || C.teal,
            cursor: 'pointer',
            border: 'none',
            bgcolor: 'transparent',
            fontFamily: 'inherit',
            p: 0,
            flexShrink: 0,
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {actionLabel}
        </Typography>
      ) : null}
    </Box>
    <Box sx={{ px: '10px', py: '8px', flex: 1, minHeight: 0, overflow: 'auto' }}>{children}</Box>
  </Box>
);

const RecentActivity = ({ items, onViewAll }) => (
  <OverviewSnapshotCard iconBg="#EDE9FE" iconEmoji="⚡" title="Recent Activity" action={onViewAll} actionLabel="View all ›">
    {!items?.length ? (
      <Typography sx={{ ...T.snapshotBody, fontStyle: 'italic', color: C.slate4 }}>No recent activity</Typography>
    ) : null}
    {items.map((a, i) => (
      <Box key={i} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', py: '9px', borderBottom: i < items.length - 1 ? `1px solid ${C.borderSubtle}` : 'none' }}>
        <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: DOT_STYLES[a.dot]?.bg || C.tealLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>
          {a.emoji}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {a.activityTitle ? (
            <Typography sx={{ ...T.snapshotTitle, mb: a.activityBody ? '6px' : 0 }}>{a.activityTitle}</Typography>
          ) : null}
          {a.activityBody ? (
            <Typography
              component="div"
              sx={{
                ...T.snapshotBody,
                '& strong, & b': { fontWeight: 500 },
                '& p': { margin: 0 },
              }}
              dangerouslySetInnerHTML={{ __html: a.activityBody }}
            />
          ) : (
            <Typography sx={{ ...T.snapshotBody, fontStyle: 'italic', color: C.slate4 }}>—</Typography>
          )}
          <Typography sx={{ ...T.snapshotMeta, mt: '3px' }}>{a.time}</Typography>
        </Box>
      </Box>
    ))}
  </OverviewSnapshotCard>
);

const aggregateTopicSentimentsForCloud = (list) => {
  if (!Array.isArray(list) || !list.length) return [];
  const m = new Map();
  list.forEach((row) => {
    const topic = String(row?.topic || 'Topic').trim() || 'Topic';
    const sentiment = String(row?.sentiment || 'neutral').toLowerCase();
    const k = `${topic}\u0000${sentiment}`;
    m.set(k, (m.get(k) || 0) + 1);
  });
  return Array.from(m.entries()).map(([k, count]) => {
    const [topic, sentiment] = k.split('\u0000');
    return { topic, sentiment, count };
  });
};

/** Latest-call `topic_sentiments` + `topics_discussed` (General_Insights) → three UI buckets */
const deriveLastCallTopicBuckets = (topicSentiments, topicsDiscussed) => {
  const seenP = new Set();
  const seenN = new Set();
  const positiveTopics = [];
  const negativeTopics = [];
  (Array.isArray(topicSentiments) ? topicSentiments : []).forEach((row) => {
    const t = String(row?.topic || '').trim();
    if (!t) return;
    const s = String(row?.sentiment || '').toLowerCase();
    if (s === 'positive' && !seenP.has(t)) {
      seenP.add(t);
      positiveTopics.push(t);
    } else if (s === 'negative' && !seenN.has(t)) {
      seenN.add(t);
      negativeTopics.push(t);
    }
  });
  const discussedRaw = Array.isArray(topicsDiscussed) ? topicsDiscussed : [];
  let topicsDiscussedList = [
    ...new Set(discussedRaw.map((x) => String(x ?? '').trim()).filter(Boolean)),
  ];
  if (!topicsDiscussedList.length && Array.isArray(topicSentiments) && topicSentiments.length) {
    topicsDiscussedList = [
      ...new Set(
        topicSentiments.map((row) => String(row?.topic || '').trim()).filter(Boolean),
      ),
    ];
  }
  return { positiveTopics, negativeTopics, topicsDiscussedList };
};

/** Positive / Negative / Topics discussed — last assessed call (matches overview `lead_conversion`) */
const LastCallTopicsSentimentCard = ({ positiveTopics, negativeTopics, topicsDiscussedList }) => (
  <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', p: '16px 18px', mt: 2 }}>
    <Box sx={{ mb: 1.5 }}>
      <OverviewCardTitleWithIcon
        icon="💬"
        iconBg="#E0F2FE"
        title="Topics & sentiment · last assessed call"
      />
    </Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        gap: { xs: 2, md: 2.5 },
      }}
    >
      <Box>
        <Typography sx={{ ...T.innerLabel, mb: 1, color: '#166534', letterSpacing: '0.06em' }}>Positive</Typography>
        <OverviewChipList items={positiveTopics} color={C.greenDark} bgColor={C.greenLight} borderColor="#86EFAC" />
      </Box>
      <Box sx={{ pt: { xs: 0, md: 0 }, borderTop: { xs: `1px solid ${C.borderSubtle}`, md: 'none' } }}>
        <Typography sx={{ ...T.innerLabel, mb: 1, color: C.redDark, letterSpacing: '0.06em' }}>Negative</Typography>
        <OverviewChipList items={negativeTopics} color={C.redDark} bgColor={C.redLight} borderColor="#FECACA" />
      </Box>
      <Box sx={{ pt: { xs: 0, md: 0 }, borderTop: { xs: `1px solid ${C.borderSubtle}`, md: 'none' } }}>
        <Typography sx={{ ...T.innerLabel, mb: 1, color: C.purple, letterSpacing: '0.06em' }}>
          Topics discussed
        </Typography>
        <OverviewChipList items={topicsDiscussedList} color={C.purple} bgColor={C.purpleLight} borderColor="#DDD6FE" />
      </Box>
    </Box>
  </Box>
);

const topicSentimentChipPalette = (sentiment) => {
  const s = String(sentiment || '').toLowerCase();
  if (s === 'positive')
    return { bg: C.greenLight, fg: C.greenDark, bdr: '#86EFAC' };
  if (s === 'negative')
    return {
      bg: '#FEE2E2',
      fg: '#B91C1C',
      bdr: '#F87171',
    };
  return { bg: C.amberLight, fg: '#92400E', bdr: '#FDE68A' };
};

/** Chip text: topic only (plus ×count for multiple mentions). Sentiment is chip color + legend, not in the label. */
const formatTopicSentimentChipLabel = (row) => {
  const cnt = Math.max(1, Number(row.count ?? row.value) || 1);
  let topic = String(row.topic || '').trim();
  if (!topic || topic === 'Topic') {
    const raw = row.label != null ? String(row.label).trim() : '';
    topic = raw.replace(/\s*·\s*(positive|neutral|negative)\s*$/i, '').trim() || topic || 'Topic';
  }
  if (cnt > 1) return `${topic} ×${cnt}`;
  return topic;
};

/** Preferences strip (row 3 in overview col 2). */
const PreferencesOverviewPanel = ({
  allPreferences,
  interestsData,
  tagsData,
  intentsData,
  amenitiesData,
  locationData,
  readOnly,
  /** Junk leads: Tags + Intents each full width (separate rows) to use horizontal space */
  stackTagsAndIntents = false,
  openPreferencesDialog,
  openInterestsDialog,
  openTagsDialog,
  openIntentsDialog,
  openAmenitiesDialog,
  openLocationDialog,
}) => (
  <Box sx={{ ...EXEC_OVERVIEW_ROW_CARD_SX, flex: 1, minHeight: 0 }}>
    <Box sx={EXEC_OVERVIEW_HEADER_ROW_SX}>
      <OverviewCardTitleWithIcon icon="⭐" iconBg="#FEF3C7" title="Preferences & interests" sx={{ flex: 1, minWidth: 0 }} boxSize={28} />
      <Box sx={EXEC_OVERVIEW_HEADER_ACTION_SLOT_SX} aria-hidden />
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, overflow: 'auto', minHeight: 0 }}>
      <OverviewAttributeCard title="Preferences" iconEmoji="🏠" iconBg="#EEF2FF" accentColor="#4F46E5" actionLabel="Edit" actionColor="#4F46E5" onAction={readOnly ? undefined : openPreferencesDialog} readOnly={readOnly}>
        <OverviewChipList items={allPreferences} color={C.blue} />
      </OverviewAttributeCard>
      <OverviewAttributeCard title="Location" iconEmoji="📍" iconBg="#ECFDF5" accentColor="#059669" actionLabel="Edit" actionColor="#059669" onAction={readOnly ? undefined : openLocationDialog} readOnly={readOnly}>
        <OverviewChipList items={locationData} color="#065F46" bgColor="#D1FAE5" borderColor="#6EE7B7" />
      </OverviewAttributeCard>
      <OverviewAttributeCard title="Interests" iconEmoji="❤️" iconBg="#FCE7F3" accentColor="#EC4899" actionLabel="Edit" actionColor="#EC4899" onAction={readOnly ? undefined : openInterestsDialog} readOnly={readOnly}>
        <OverviewChipList items={interestsData} color={C.red} bgColor={C.redLight} borderColor="#FECACA" />
      </OverviewAttributeCard>
      <Box
        sx={
          stackTagsAndIntents
            ? { display: 'flex', flexDirection: 'column', gap: 1 }
            : { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }
        }
      >
        <OverviewAttributeCard title="Tags" iconEmoji="🏷" iconBg="#E6FFFB" accentColor="#0D9488" actionLabel="Edit" actionColor="#0D9488" onAction={readOnly ? undefined : openTagsDialog} readOnly={readOnly}>
          <OverviewChipList items={tagsData} color={C.slate3} bgColor={C.bg} borderColor={C.border} />
        </OverviewAttributeCard>
        <OverviewAttributeCard title="Intents" iconEmoji="🔥" iconBg="#FFF7ED" accentColor="#F59E0B" actionLabel="Edit" actionColor="#F59E0B" onAction={readOnly ? undefined : openIntentsDialog} readOnly={readOnly}>
          <OverviewChipList items={intentsData} color={C.amber} bgColor={C.amberLight} borderColor="#FDE68A" />
        </OverviewAttributeCard>
      </Box>
      <OverviewAttributeCard title="Amenities" iconEmoji="🏡" iconBg="#F3E8FF" accentColor="#8B5CF6" actionLabel="Edit" actionColor="#8B5CF6" onAction={readOnly ? undefined : openAmenitiesDialog} readOnly={readOnly}>
        <OverviewChipList items={amenitiesData} color={C.purple} bgColor={C.purpleLight} borderColor="#DDD6FE" />
      </OverviewAttributeCard>
    </Box>
  </Box>
);

/** `lead_aging.is_dormant` — matches schedule-missed / caution tone */
const agingDormantChipSx = {
  ...T.chip,
  height: 24,
  fontSize: '0.65rem',
  fontWeight: 800,
  bgcolor: '#FFEDD5',
  color: '#9A3412',
  border: '1px solid #FDBA74',
  textTransform: 'capitalize',
};

/** Lead aging body rows — label left, value right; bold when present (reference card). */
const AgingCardRow = ({ label, value, isLast, valueColor }) => {
  const v = String(value ?? '').trim() || '—';
  const isEmpty = v === '—';
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 2,
        py: '10px',
        borderBottom: isLast ? 'none' : `1px solid ${C.borderSubtle}`,
      }}
    >
      <Typography sx={{ fontSize: '12px', color: C.slate4, fontWeight: 500, flexShrink: 0 }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: isEmpty ? 500 : 700,
          color: valueColor || (isEmpty ? C.slate4 : C.slate2),
          textAlign: 'right',
          lineHeight: 1.35,
        }}
      >
        {v}
      </Typography>
    </Box>
  );
};

const AgingEngagementCard = ({ leadAgeDays, agingStatusChip, la, statsData }) => {
  const created = abbreviateLongMonthNamesInString(la?.lead_created || statsData.lead_created_date || '—');
  const firstC = abbreviateLongMonthNamesInString(la?.first_contacted_at || statsData.first_contacted_date || '—');
  const lastC = abbreviateLongMonthNamesInString(la?.last_contacted_at || la?.last_contact || statsData.last_contact_date || '—');

  return (
    <Box
      sx={{
        bgcolor: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: '14px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        p: '18px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ mb: 1.25 }}>
        <OverviewCardTitleWithIcon icon="📅" iconBg={C.blueLight} title="Lead aging" />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1.25,
          flexWrap: 'wrap',
          pb: 1.5,
          mb: 0,
          borderBottom: `1px solid ${C.borderSubtle}`,
        }}
      >
        <Typography sx={{ ...T.heroMetric, color: C.blue, lineHeight: 1 }}>{leadAgeDays != null ? `${leadAgeDays}d` : '—'}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0 }}>
          {agingStatusChip ? <Box sx={{ flexShrink: 0 }}>{agingStatusChip}</Box> : null}
          {la?.is_dormant ? <Chip label="Dormant" size="small" sx={agingDormantChipSx} /> : null}
        </Box>
      </Box>

      <Box sx={{ flex: 1, pt: 0.5 }}>
        <AgingCardRow label="Lead Created On" value={created} />
        <AgingCardRow label="First Contacted On" value={firstC} />
        <AgingCardRow label="Last Contacted On" value={lastC} />
      </Box>
    </Box>
  );
};

const TopicSentimentCloudCard = ({ topicRows, title = 'Topic Sentiment' }) => (
  <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', p: '16px 18px', mt: 2 }}>
    <Box sx={{ mb: 1.25 }}>
      <OverviewCardTitleWithIcon icon="💬" iconBg={C.purpleLight} title={title} />
    </Box>
    {!topicRows?.length ? (
      <Typography sx={{ ...T.bodyPrimary, fontStyle: 'italic', color: C.slate4, fontWeight: 500 }}>Topics will appear after calls are processed with AI insights.</Typography>
    ) : (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mb: 1.25, alignItems: 'center' }}>
        {topicRows.map((row, i) => {
          const sentiment = row.sentiment || 'neutral';
          const pal = topicSentimentChipPalette(sentiment);
          const displayLabel = formatTopicSentimentChipLabel(row);
          return (
            <Chip
              key={`${row.topic || displayLabel}-${sentiment}-${i}`}
              label={displayLabel}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.625rem',
                fontWeight: 600,
                borderRadius: '10px',
                bgcolor: pal.bg,
                color: pal.fg,
                border: `1px solid ${pal.bdr}`,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          );
        })}
      </Box>
    )}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', pt: 1, borderTop: `1px solid ${C.borderSubtle}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '999px', bgcolor: C.greenDark }} />
        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: C.slate3 }}>Positive</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '999px', bgcolor: '#D97706' }} />
        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: C.slate3 }}>Neutral</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '999px', bgcolor: '#B91C1C' }} />
        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: C.slate3 }}>Negative</Typography>
      </Box>
    </Box>
  </Box>
);

// ─── MAIN ─────────────────────────────────────────────────────────────────────

const LeadOverview = ({ leadId, onSwitchTab, openTaskModal, setOpenTaskModal, leadsDefaultFilters = null }) => {
  const {
    overview,
    loading,
    leadDetails,
    fetchLeadNotes,
    fetchLeadDetails,
    stats,
    preferences,
    restoreLead,
    markLeadNotRecoverable,
  } = useLeadDetails();
  const [showAddNotes, setShowAddNotes] = useState(false);
  const [junkMoveBackOpen, setJunkMoveBackOpen] = useState(false);
  const [projectUnits, setProjectUnits] = useState([]);
  const [prefDialogOpen, setPrefDialogOpen] = useState(false);
  const [interestsDialogOpen, setInterestsDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [intentsDialogOpen, setIntentsDialogOpen] = useState(false);
  const [amenitiesDialogOpen, setAmenitiesDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [taskDefaultItemType, setTaskDefaultItemType] = useState(null);

  const { allPreferences, interestsData, tagsData, intentsData, amenitiesData, locationData } = useMemo(() => {
    const saved = preferences?.saved_preferences || {};
    return {
      allPreferences: saved?.preferences || [],
      interestsData: saved?.interests || [],
      tagsData: saved?.tags || [],
      intentsData: saved?.intents || [],
      amenitiesData: saved?.amenities || [],
      locationData: saved?.location || [],
    };
  }, [preferences]);

  const openPreferencesDialog = useCallback(() => setPrefDialogOpen(true), []);
  const openInterestsDialog = useCallback(() => setInterestsDialogOpen(true), []);
  const openTagsDialog = useCallback(() => setTagsDialogOpen(true), []);
  const openIntentsDialog = useCallback(() => setIntentsDialogOpen(true), []);
  const openAmenitiesDialog = useCallback(() => setAmenitiesDialogOpen(true), []);
  const openLocationDialog = useCallback(() => setLocationDialogOpen(true), []);

  useEffect(() => {
    fetchProjectUnitSelect()
      .then((d) => setProjectUnits(d || []))
      .catch(() => setProjectUnits([]));
  }, []);

  const statsData = stats || {};
  const lead = mergeDialNumberIntoLeadContactInfo(leadDetails);
  const stg = normalizeStageKey(leadDetails?.stage);
  const stStatus = String(leadDetails?.status || '').toLowerCase().replace(/\s+/g, '_').trim();
  const isNotInterested = stStatus === 'not_interested';
  const bandForcedLow = isNotInterested || stg === 'junk' || stg === 'archived';

  const conversionProb = overview?.conversion_probability || null;
  const leadConversionForScore = overview?.lead_conversion || null;
  const conversationInsights = overview?.conversation_insights || null;

  const conversionScoreForUi = useMemo(() => {
    if (conversionProb?.score != null && conversionProb.score !== '') {
      const n = Number(conversionProb.score);
      if (!Number.isNaN(n)) return Math.round(Math.max(0, Math.min(100, n)));
    }
    const nv = normalizeLeadConversionForCard(leadConversionForScore);
    return nv?.leadScore != null ? nv.leadScore : null;
  }, [conversionProb?.score, leadConversionForScore]);

  const coveragePct = conversionScoreForUi ?? 0;
  const band =
    conversionProb?.band ||
    (bandForcedLow ? 'low' : conversionScoreForUi != null ? conversionBandFromScore(conversionScoreForUi) : 'medium');

  const positiveSignals = useMemo(() => {
    const meta = overview?.positive_signals ?? [];
    const out = Array.isArray(meta) ? meta : [];
    return [...new Set(out.map((x) => String(x || '').trim()).filter(Boolean))];
  }, [overview?.positive_signals]);

  const negativeSignals = useMemo(() => {
    const meta = overview?.negative_signals ?? [];
    const out = Array.isArray(meta) ? meta : [];
    return [...new Set(out.map((x) => String(x || '').trim()).filter(Boolean))];
  }, [overview?.negative_signals]);

  const copilot = useMemo(
    () =>
      buildLeadOverviewCopilotModel({
        leadDetails: lead,
        overview,
        stats: statsData,
        conversionProb,
        conversationInsights,
        positiveSignals,
        negativeSignals,
      }),
    [lead, overview, statsData, conversionProb, conversationInsights, positiveSignals, negativeSignals],
  );

  const daysSince = overview?.lead_aging?.days_since_last_contact ?? statsData.days_since_last_contact ?? null;
  const la = overview?.lead_aging;
  /** Lead aging hero "Nd": lead_age_days first; then days_since_last_contact so 0 still renders as 0d. */
  const agingHeroDays =
    la?.lead_age_days ??
    statsData.lead_age_days ??
    la?.days_since_last_contact ??
    statsData.days_since_last_contact ??
    null;
  const isJunkOrArchived = stg === 'junk' || stg === 'archived';
  const isJunk = stg === 'junk';
  const isDeleted = Boolean(leadDetails?.is_deleted);
  /** Trash or junk: overview prefs & activity shortcuts read-only */
  const limitedOverviewInteractions = isDeleted || isJunk;

  const dial = getLeadDialNumber(lead);
  const dialDigits = String(dial || '').replace(/\D/g, '');

  const handleOverviewQuickAction = (key) => {
    if (limitedOverviewInteractions) return;
    switch (key) {
      case 'lead_followup':
        setTaskDefaultItemType('lead_followup');
        setOpenTaskModal?.(true);
        break;
      case 'site_visit':
        setTaskDefaultItemType('site_visit');
        setOpenTaskModal?.(true);
        break;
      case 'document_collection':
        setTaskDefaultItemType('document_collection');
        setOpenTaskModal?.(true);
        break;
      case 'properties':
        onSwitchTab?.('properties');
        break;
      case 'call':
        if (dial) window.location.href = `tel:${dial}`;
        break;
      case 'schedule':
      case 'recover':
        setTaskDefaultItemType(null);
        setOpenTaskModal?.(true);
        break;
      case 'whatsapp':
        onSwitchTab?.('whatsapp');
        if (dialDigits.length >= 10) {
          window.open(`https://wa.me/${dialDigits}`, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'archive':
        onSwitchTab?.('activity');
        break;
      default:
        break;
    }
  };

  const recent_call = overview?.recent_call || null;
  const upcoming_task = overview?.upcoming_task || null;
  const latest_notes = overview?.latest_notes || null;
  const latest_activity = overview?.latest_activity || null;

  const callsData = useMemo(() => {
    if (!recent_call) return [];
    const base = mapApiRecentCallToViewModel(recent_call);
    const detailText = getRecentCallSummaryText(recent_call, latest_activity);
    return [{ ...base, detailText: detailText || null }];
  }, [recent_call, latest_activity]);
  const notesData = latest_notes ? [mapApiNoteToViewModel(latest_notes)].filter(Boolean) : [];
  const tasksData = upcoming_task ? [mapApiTaskToViewModel(upcoming_task)].filter(Boolean) : [];
  const activityData = latest_activity ? [mapApiActivityToViewModel(latest_activity)].filter(Boolean) : [];

  const leadConversion = overview?.lead_conversion || null;
  const conversionReadiness =
    leadConversion?.conversion_readiness ?? leadConversion?.general_insights?.conversion_readiness ?? null;

  const topicSentimentRows = useMemo(() => {
    const tags = overview?.sentiment_and_topics?.topic_cloud_tags;
    if (Array.isArray(tags) && tags.length) {
      return tags
        .map((t) => ({
          label: t?.label != null ? String(t.label).trim() : null,
          topic: String(t?.topic || '').trim() || 'Topic',
          sentiment: String(t?.sentiment || 'neutral').toLowerCase(),
          count: Math.max(1, Number(t?.value ?? t?.count) || 1),
          weight: typeof t?.weight === 'number' && !Number.isNaN(t.weight) ? t.weight : undefined,
        }))
        .filter((r) => Boolean(r.topic) || Boolean(r.label));
    }
    const legacy = aggregateTopicSentimentsForCloud(
      overview?.lead_conversion?.general_insights?.topic_sentiments,
    );
    if (!legacy.length) return [];
    const maxLegacy = Math.max(...legacy.map((r) => r.count), 0);
    return legacy.map((r) => ({
      ...r,
      weight: maxLegacy ? Math.max(0.35, Math.min(1, r.count / maxLegacy)) : 0.5,
    }));
  }, [
    overview?.sentiment_and_topics?.topic_cloud_tags,
    overview?.lead_conversion?.general_insights?.topic_sentiments,
  ]);

  const niRecovery = useMemo(
    () => normalizeOverviewLeadRecovery(overview, leadDetails),
    [overview, leadDetails],
  );

  const recoveryReadinessFromLeadRecovery = useMemo(
    () => getRecoveryReadinessBarsFromOverview(overview),
    [overview],
  );

  if (loading?.overview) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ ...T.bodySecondary }}>Loading overview…</Typography>
      </Box>
    );
  }

  const canMarkNotRecoverable = !Boolean(leadDetails?.is_not_recoverable);
  const canRecoverLead =
    canMarkNotRecoverable &&
    (isNotInterested
      ? niRecovery != null
        ? Boolean(niRecovery.recoverable)
        : Boolean(leadDetails?.is_currently_recoverable)
      : true);

  const veryStaleClosureHint =
    typeof daysSince === 'number' &&
    daysSince > 40 &&
    (statsData.engagement_score ?? 0) < 20 &&
    band === 'low';


  const assessedLabel =
    (overview?.latest_activity?.date ? `Assessed ${overview.latest_activity.date}` : null) ||
    (overview?.latest_notes?.date ? `Assessed ${overview.latest_notes.date}` : null) ||
    null;



  const recoveryScore = niRecovery?.recovery_score ?? null;
  const recoverable = Boolean(niRecovery?.recoverable);
  const useRecoveryCard = !isDeleted && (isJunkOrArchived || isNotInterested);

  const junkStatusChipSx = {
    ...T.chip,
    fontSize: '0.65rem',
    fontWeight: 800,
    textTransform: 'none',
    letterSpacing: '0.02em',
    bgcolor: '#FFFBEB',
    color: '#92400E',
    border: '1px solid #FDE68A',
  };

  const leadRecoveryCardHeaderRight = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {isJunk && !isDeleted ? (
        <Button
          variant="contained"
          size="small"
          disableElevation
          onClick={() => setJunkMoveBackOpen(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: '10px',
            fontSize: '11px',
            py: 0.65,
            px: 1.25,
            bgcolor: C.greenDark,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#15803D', boxShadow: 'none' },
          }}
        >
          Move to qualify →
        </Button>
      ) : null}
      {isJunk ? (
        <Chip label="Junk" size="small" sx={junkStatusChipSx} />
      ) : stg === 'archived' ? (
        <Chip
          label="Archived"
          size="small"
          sx={{
            ...T.chip,
            fontSize: '0.65rem',
            fontWeight: 800,
            textTransform: 'none',
            bgcolor: C.bg2,
            color: C.slate3,
            border: `1px solid ${C.border}`,
          }}
        />
      ) : isNotInterested ? (
        <Chip
          label="Not interested"
          size="small"
          sx={{
            ...T.chip,
            fontSize: '0.65rem',
            fontWeight: 800,
            textTransform: 'none',
            bgcolor: '#FEF2F2',
            color: '#991B1B',
            border: '1px solid #FECACA',
          }}
        />
      ) : null}
    </Box>
  );

  const recoveryConvNorm = normalizeLeadConversionForCard(overview?.lead_conversion);
  const recoveryBarsFallbackAi = recoveryConvNorm?.leadScore ?? conversionProb?.score ?? null;
  const recoveryBarsFallbackRule =
    recoveryScore ??
    (statsData?.engagement_score != null ? Number(statsData.engagement_score) : null) ??
    conversionProb?.score ??
    null;

  const niRecoveryActionFooter =
    isNotInterested && niRecovery && !isDeleted ? (
      <Box
        sx={{
          borderTop: `1px solid ${C.borderSubtle}`,
          bgcolor: '#FFF7F7',
          mt: 1.5,
          pt: 1.5,
          mx: -0.5,
          px: 1.5,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            disabled={!canMarkNotRecoverable}
            onClick={() => markLeadNotRecoverable?.(leadId)}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: '12px',
              px: 1.75,
              ...(!canMarkNotRecoverable && { opacity: 0.55 }),
            }}
          >
            Mark not recoverable
          </Button>
        </Box>
        {assessedLabel ? (
          <Typography sx={{ ...T.bodyPrimary, fontWeight: 700 }}>{assessedLabel}</Typography>
        ) : null}
      </Box>
    ) : null;


  // ── Active lead mode — executive dashboard layout (reference UI) ─────────────
  const agingStatusChipActive =
    la?.aging_status ? (
      <Chip
        label={la.aging_status}
        size="small"
        sx={{
          ...T.chip,
          height: 24,
          fontSize: '0.7rem',
          fontWeight: 700,
          bgcolor: String(la.aging_status).toLowerCase() === 'active' ? C.blueLight : C.bg2,
          color: String(la.aging_status).toLowerCase() === 'active' ? '#1E40AF' : C.slate3,
          border: `1px solid ${String(la.aging_status).toLowerCase() === 'active' ? '#BFDBFE' : C.border}`,
          textTransform: 'capitalize',
        }}
      />
    ) : la?.in_stage ? (
      <Chip
        label={`${String(la.in_stage).toLowerCase()} stage`}
        size="small"
        sx={{
          ...T.chip,
          height: 24,
          bgcolor: C.bg2,
          color: C.slate3,
          border: `1px solid ${C.border}`,
          textTransform: 'capitalize',
        }}
      />
    ) : null;

  return (
    <Box sx={{ p: { xs: '16px', sm: '22px 28px' }, pb: '48px', bgcolor: C.bg, minHeight: '100%' }}>
      {isDeleted ? (
        <>
          <LeadRestoreComponent
            leadDetails={leadDetails}
            leadAgeDays={agingHeroDays}
            latestActivity={latest_activity}
            onRestore={() => restoreLead?.(leadId)}
            canRecover={canRecoverLead}
          />
          {niRecovery && !niRecovery.recoverable ? <DeletedNotRecoverableInsightCard recovery={niRecovery} /> : null}
        </>
      ) : null}

      {!isDeleted ? (
        <ConversationInsightsTop
          conversationInsights={conversationInsights}
          onPrimaryAction={() => onSwitchTab?.('activity')}
        />
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: '16px',
          alignItems: 'stretch',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
          {useRecoveryCard ? (
            <LeadRecovery
              variant="executive"
              headerRight={leadRecoveryCardHeaderRight}
              recoveryScore={recoveryScore}
              recoverable={recoverable}
              niRecovery={niRecovery}
              isNotInterested={isNotInterested}
              isJunkOrArchived={isJunkOrArchived}
              conversionReadiness={recoveryReadinessFromLeadRecovery ?? conversionReadiness}
              recoveryBarsFallbackAi={recoveryBarsFallbackAi}
              recoveryBarsFallbackRule={recoveryBarsFallbackRule}
              niActionFooter={niRecoveryActionFooter}
            />
          ) : (
            <LeadCoverage
              coveragePct={coveragePct}
              leadConversion={leadConversion}
              sentimentAndTopics={overview?.sentiment_and_topics}
              veryStaleClosureHint={veryStaleClosureHint}
            />
          )}
          <AgingEngagementCard
            leadAgeDays={agingHeroDays}
            agingStatusChip={agingStatusChipActive}
            la={la}
            statsData={statsData}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0, height: '100%', minHeight: 0 }}>
          <LeadTemperature leadTemperature={overview?.lead_temperature} />
          <LeadRecentNotes
            notes={notesData}
            onAddNote={limitedOverviewInteractions ? undefined : () => setShowAddNotes(true)}
            readOnly={limitedOverviewInteractions}
          />
          <PreferencesOverviewPanel
            allPreferences={allPreferences}
            interestsData={interestsData}
            tagsData={tagsData}
            intentsData={intentsData}
            amenitiesData={amenitiesData}
            locationData={locationData}
            readOnly={limitedOverviewInteractions}
            stackTagsAndIntents={isJunk}
            openPreferencesDialog={openPreferencesDialog}
            openInterestsDialog={openInterestsDialog}
            openTagsDialog={openTagsDialog}
            openIntentsDialog={openIntentsDialog}
            openAmenitiesDialog={openAmenitiesDialog}
            openLocationDialog={openLocationDialog}
          />
        </Box>
      </Box>

      <TopicSentimentCloudCard
        topicRows={topicSentimentRows}
        title={
          Array.isArray(overview?.sentiment_and_topics?.topic_cloud_tags) &&
          overview.sentiment_and_topics.topic_cloud_tags.length
            ? 'Topic sentiment · all processed calls'
            : 'Topic Discussed'
        }
      />

      <OverviewSection
        title="Recent activity"
        sectionIcon="⚡"
        sectionIconBg="#EDE9FE"
        compact
        subtitle="Calls, calendar task, and latest activity log."
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '14px' }}>
          <RecentCalls
            calls={callsData}
            onViewAll={limitedOverviewInteractions ? undefined : () => onSwitchTab?.('calls')}
          />
          <UpcomingTask
            tasks={tasksData}
            onNewTask={
              limitedOverviewInteractions
                ? undefined
                : () => {
                    setTaskDefaultItemType(null);
                    setOpenTaskModal?.(true);
                  }
            }
          />
          <RecentActivity
            items={activityData}
            onViewAll={limitedOverviewInteractions ? undefined : () => onSwitchTab?.('activity')}
          />
        </Box>
      </OverviewSection>

      <AddNotes
        open={showAddNotes}
        onClose={() => setShowAddNotes(false)}
        leadIds={[leadDetails?.id]}
        onComplete={() => {
          setShowAddNotes(false);
          fetchLeadNotes(leadId);
        }}
      />

      {openTaskModal && (
        <CreateNewTask
          open={openTaskModal}
          onClose={() => {
            setOpenTaskModal(false);
            setTaskDefaultItemType(null);
          }}
          leadId={leadId}
          lead={leadDetails}
          source="lead"
          defaultItemType={taskDefaultItemType}
        />
      )}

      <PreferenceUpdate open={prefDialogOpen} onClose={() => setPrefDialogOpen(false)} leadId={leadId} />
      <InterestsUpdate open={interestsDialogOpen} onClose={() => setInterestsDialogOpen(false)} leadId={leadId} />
      <TagsUpdate open={tagsDialogOpen} onClose={() => setTagsDialogOpen(false)} leadId={leadId} leadTags={leadsDefaultFilters?.tags || []} />
      <IntentsUpdate open={intentsDialogOpen} onClose={() => setIntentsDialogOpen(false)} leadId={leadId} />
      <AmenitiesUpdate open={amenitiesDialogOpen} onClose={() => setAmenitiesDialogOpen(false)} leadId={leadId} />
      <LocationUpdate open={locationDialogOpen} onClose={() => setLocationDialogOpen(false)} leadId={leadId} />

      <UpdateStatus
        open={junkMoveBackOpen}
        onClose={() => setJunkMoveBackOpen(false)}
        leadIds={leadDetails?.id ? [leadDetails.id] : []}
        currentStatus={leadDetails?.status}
        currentStage={leadDetails?.stage}
        leadName={leadDetails?.contact_info?.name || ''}
        leadId={leadId}
        projectUnits={projectUnits}
        targetStage="qualified"
        onComplete={() => {
          setJunkMoveBackOpen(false);
          fetchLeadDetails?.(leadId);
        }}
      />
    </Box>
  );
};

export default LeadOverview;
 